import React, { FC, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { isEmpty } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DEFAULT_DATE_TIME_FORMAT } from '@constants/common';
import { colors } from '@constants/colors';

import { DatePickerProps } from './types';

const DateTimePicker: FC<DatePickerProps> = props => {
  const {
    name,
    control,
    placeholder,
    errors,
    defaultValue,
    wrapperClass = '',
    dateFormat = DEFAULT_DATE_TIME_FORMAT,
    isDisabled = false,
    isVerified = false,
    description,
    autoFocusField = false
  } = props;

  const datePickerCustomStyles = {
    height: '52px',
    width: '100%',
    '& input': {
      height: '19px'
    },
    '& label.Mui-focused': {
      color: colors.primaryBlue
    },
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: '#000000ab'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: isVerified ?? false ? colors.errorRed : colors.gray88,
        borderRadius: '5px',
        borderWidth: '1px'
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.primaryBlue,
        borderRadius: '5px',
        borderWidth: '1px'
      }
    }
  };

  const [newDate, setNewDate] = useState<Dayjs | null>(
    defaultValue ? dayjs(defaultValue) : null
  );

  useEffect(() => {
    if (defaultValue) {
      setNewDate(dayjs(defaultValue));
    } else {
      setNewDate(null);
    }
  }, [defaultValue]);

  return (
    <div className={`w-full ${wrapperClass}`}>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange }, fieldState: { error } }) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MuiDateTimePicker
              label={placeholder}
              views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
              value={newDate}
              onChange={newValue => {
                setNewDate(newValue);
                onChange(newValue);
              }}
              slotProps={{
                textField: {
                  error: !!error,
                  size: 'medium'
                }
              }}
              format={dateFormat}
              sx={datePickerCustomStyles}
              disabled={isDisabled}
              autoFocus={autoFocusField}
              ampm={true}
            />
          </LocalizationProvider>
        )}
      />
      {!isEmpty(errors) && (errors[name] as { message?: string })?.message ? (
        <p className="mx-[14px] mt-[3px] text-xs leading-5 text-red">
          {(errors[name] as { message?: string })?.message}
        </p>
      ) : (
        description && (
          <p
            className={`mt-[3px] mx-[14px] leading-5 text-xs text-black ${
              isDisabled ? 'text-opacity-40' : 'text-opacity-60'
            }`}>
            {description}
          </p>
        )
      )}
    </div>
  );
};

export default DateTimePicker;
